import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { SystemHealthDto } from '@eskoolhub/types';
import { PageHeader } from '../components/AppShell';
import { useToast } from '../components/Toasts';
import { ErrorState, SERVICE_STATE_COLOUR, SkeletonRows, SkeletonTiles, StatusDot } from '../components/ui';
import { api, ApiRequestError } from '../lib/api';
import { formatBps, formatTime } from '../lib/format';

export function SystemHealthPage() {
  const toast = useToast();
  const queryClient = useQueryClient();

  const { data, isPending, error, refetch } = useQuery({
    queryKey: ['system-health'],
    queryFn: () => api.get<SystemHealthDto>('/api/system/health'),
    refetchInterval: 30_000,
  });

  const updateIncident = useMutation({
    mutationFn: ({ id, status }: { id: string; status: 'MONITORING' | 'RESOLVED' }) =>
      api.patch(`/api/system/incidents/${id}`, { status }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['system-health'] });
      toast.push('Incident updated', 'success');
    },
    onError: (err) => toast.push(err instanceof ApiRequestError ? err.message : 'Could not update the incident', 'error'),
  });

  const peakP95 = data?.latency.points.reduce((max, point) => Math.max(max, point.p95Ms), 0) ?? 1;

  return (
    <>
      <PageHeader
        title="System health"
        meta="All services · live"
        actions={
          data ? (
            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              <StatusDot state={data.overall} />
              <span style={{ fontSize: 13.5, fontWeight: 700, color: SERVICE_STATE_COLOUR[data.overall] }}>
                {data.overallLabel}
              </span>
            </div>
          ) : null
        }
      />

      <div className="page">
        {error ? (
          <ErrorState
            message={error instanceof ApiRequestError ? error.message : 'Could not load system health'}
            onRetry={() => void refetch()}
          />
        ) : isPending ? (
          <>
            <SkeletonTiles />
            <SkeletonRows count={2} />
          </>
        ) : (
          <>
            <div className="grid-4">
              {data.services.map((service) => (
                <div key={service.service} className="tile">
                  <div style={{ display: 'flex', alignItems: 'center', gap: 9 }}>
                    <StatusDot state={service.state} />
                    <span className="label" style={{ letterSpacing: '0.08em' }}>
                      {service.label}
                    </span>
                  </div>
                  <div
                    className="tile__value"
                    style={{ fontSize: 26, color: service.state === 'OPERATIONAL' ? undefined : SERVICE_STATE_COLOUR[service.state] }}
                  >
                    {formatBps(service.successRateBps, 2)}
                  </div>
                  <div className="caption">{service.note ?? service.windowLabel}</div>
                </div>
              ))}
            </div>

            {data.activeIncidents.map((incident) => (
              <div
                key={incident.id}
                className="card"
                style={{ borderLeft: '5px solid var(--marigold-deep)', boxShadow: 'var(--shadow-card)', gap: 12 }}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
                  <div className="card__title">{incident.title}</div>
                  <span className="chip" style={{ background: 'var(--marigold)', color: 'var(--ink)' }}>
                    {incident.status.charAt(0) + incident.status.slice(1).toLowerCase()}
                  </span>
                  <span className="mono caption" style={{ marginLeft: 'auto' }}>
                    Started {formatTime(incident.startedAt)}
                  </span>
                </div>
                <div style={{ fontSize: 14, lineHeight: 1.55, color: 'var(--ink-soft)' }}>{incident.summary}</div>
                <div style={{ display: 'flex', gap: 10 }}>
                  <button
                    className="btn btn--soft"
                    onClick={() => updateIncident.mutate({ id: incident.id, status: 'MONITORING' })}
                    disabled={updateIncident.isPending || incident.status === 'MONITORING'}
                  >
                    Mark monitoring
                  </button>
                  <button
                    className="btn btn--sm"
                    onClick={() => updateIncident.mutate({ id: incident.id, status: 'RESOLVED' })}
                    disabled={updateIncident.isPending}
                  >
                    Resolve incident
                  </button>
                </div>
              </div>
            ))}

            <div className="card">
              <div className="card__title">API latency · last 24h</div>
              <div style={{ display: 'flex', alignItems: 'flex-end', gap: 6, height: 150 }}>
                {data.latency.points.map((point) => {
                  const isPeak = data.latency.peak?.bucketedAt === point.bucketedAt;
                  const isLast = point === data.latency.points[data.latency.points.length - 1];
                  return (
                    <div
                      key={point.bucketedAt}
                      title={`${formatTime(point.bucketedAt)} · p95 ${point.p95Ms}ms · p50 ${point.p50Ms}ms`}
                      style={{
                        flex: 1,
                        height: `${Math.max(8, (point.p95Ms / peakP95) * 100)}%`,
                        background: isPeak ? 'var(--coral)' : isLast ? 'var(--ink)' : 'var(--rule)',
                        borderRadius: 5,
                      }}
                    />
                  );
                })}
              </div>
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 14,
                  borderTop: '1px solid var(--rule)',
                  paddingTop: 12,
                }}
              >
                <span className="caption">
                  {data.latency.peak
                    ? `Peak at ${formatTime(data.latency.peak.bucketedAt)} · ${data.latency.peak.p95Ms}ms p95 · ${data.latency.peak.errorCount} errors`
                    : 'No samples in this window'}
                </span>
                <span className="mono" style={{ marginLeft: 'auto', fontSize: 12.5, fontWeight: 600 }}>
                  p95: {data.latency.p95Ms}ms
                </span>
              </div>
            </div>
          </>
        )}
      </div>
    </>
  );
}
